import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState, LoadingSkeleton } from "@/components/shared/States";
import { StatusBadge } from "@/components/shared/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, Plus, Mail, Phone, Sparkles, ListChecks } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { toast } from "sonner";

const STEP_ICON = { email: Mail, task: ListChecks, call: Phone, ai: Sparkles };

export default function Sequences() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const load = async () => { setItems(null); const { data } = await api.get("/sequences"); setItems(data.items); };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!name) return toast.error("Name required");
    const steps = [{ day: 1, type: "email", label: "Intro Email" }, { day: 3, type: "task", label: "LinkedIn Touch" }, { day: 5, type: "email", label: "Follow-Up" }, { day: 8, type: "call", label: "Call" }];
    await api.post("/sequences", { name, status: "draft", steps }); toast.success("Sequence created"); setOpen(false); setName(""); load();
  };

  return (
    <div>
      <PageHeader title="Sequences" subtitle="Automated multi-step outreach" icon={Send}
        actions={<Button data-testid="new-sequence-button" className="aurelo-gradient text-white" onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" />New Sequence</Button>} />
      {items === null ? <LoadingSkeleton /> : items.length === 0 ? (
        <EmptyState icon={Send} title="No sequences yet" description="Turn manual follow-up into a repeatable, high-converting sales process." actionLabel="Create Sequence" action={() => setOpen(true)} />
      ) : (
        <div className="space-y-3">
          {items.map((s) => (
            <div key={s.id} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3"><h3 className="font-semibold text-slate-900">{s.name}</h3><StatusBadge value={s.status} /></div>
                <div className="text-xs text-slate-400">{s.owner_name}</div>
              </div>
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {(s.steps || []).map((st, i) => { const Icon = STEP_ICON[st.type] || Mail; return (
                  <div key={i} className="flex items-center gap-2 shrink-0">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 min-w-[120px]"><div className="text-[10px] text-slate-400 font-semibold">DAY {st.day}</div><div className="flex items-center gap-1.5 text-xs font-medium mt-0.5"><Icon className="h-3.5 w-3.5 text-[#7C3AED]" />{st.label}</div></div>
                    {i < s.steps.length - 1 && <div className="h-px w-4 bg-slate-200" />}
                  </div>
                ); })}
              </div>
              <div className="grid grid-cols-4 md:grid-cols-8 gap-2 mt-4 pt-4 border-t border-slate-100 text-center">
                {[["Sent", s.stats?.sent], ["Opened", s.stats?.opened], ["Clicked", s.stats?.clicked], ["Replied", s.stats?.replied], ["Meetings", s.stats?.meetings], ["Opps", s.stats?.opportunities], ["Revenue", fmtMoney(s.stats?.revenue || 0)]].map(([l, v]) => (
                  <div key={l}><div className="text-[11px] text-slate-400">{l}</div><div className="text-sm font-semibold tabular-nums">{v ?? 0}</div></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>New Sequence</DialogTitle></DialogHeader>
          <div><Label>Sequence name</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1" placeholder="Enterprise Outbound" /><p className="text-xs text-slate-400 mt-2">A 4-step starter sequence will be created. Customize steps after.</p></div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="aurelo-gradient text-white" onClick={create}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
